import type { JsonSchema, JsonValue } from '@bliksys/shared';

/**
 * The demo schema shipped with the app. In production a user provides
 * their own schema; this fixture lets the app boot into a working
 * editor for the showcase tour.
 *
 * The schema is deliberately written as a "real CMS" so the demo
 * actually demonstrates content authoring, not toy fields. It also
 * doubles as a cookbook of patterns Bliksys users can copy:
 *
 *  - Reusable subschemas via $defs (Image, Link, SEO, Publishing) —
 *    a Page references them via $ref; the schema editor's autocomplete
 *    surfaces these from /$defs.
 *  - Field grouping on rich object schemas (Page splits into Content /
 *    SEO & social / Publishing) — see x-bliksys.fieldGroups.
 *  - Tagged-union content blocks (Block oneOf with type discriminator,
 *    each variant bringing its own summaryField for clean breadcrumbs).
 *  - Inline-expanded layouts where the user benefits from seeing
 *    children in place (pages list, blocks list, columns).
 *  - Per-variant summaries so list-row previews are meaningful (heading
 *    text shows in breadcrumbs instead of "#1, #1, #1").
 *
 * If you're authoring a Bliksys schema, this is a useful starting
 * point — copy any $defs entry and adapt.
 */
export const demoSchema: JsonSchema = {
  type: 'object',
  title: 'Site',
  required: ['title'],
  // Group the site-level fields so the root form reads as a list of
  // concerns rather than a flat blob. Identity (title + tagline) and
  // Pages stay open; the more advanced site-wide settings collapse.
  'x-bliksys': {
    fieldGroups: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'How the site is named and tagged.',
        fields: ['title', 'tagline'],
      },
      {
        id: 'pages',
        title: 'Pages',
        description: 'The pages this site contains, in navigation order.',
        fields: ['pages'],
      },
      {
        id: 'navigation',
        title: 'Navigation',
        description:
          'Top-level menu of the site. Inline navigation lists pages from this site; ' +
          'external navigation links to a single off-site URL.',
        fields: ['navigation'],
      },
      {
        id: 'defaults',
        title: 'Site-wide defaults',
        description:
          'Defaults applied to every page that does not override them. Useful for ' +
          'the fallback social-card image and the default search-engine description.',
        fields: ['defaultSEO'],
        collapsed: true,
      },
      {
        id: 'social',
        title: 'Social profiles',
        description: 'Links shown in the footer and used for `og:see-also` metadata. All optional.',
        fields: ['social'],
        collapsed: true,
      },
    ],
  },
  properties: {
    title: {
      type: 'string',
      title: 'Site title',
      description: 'Used as the default browser tab title and in social-card previews.',
    },
    tagline: {
      type: 'string',
      title: 'Tagline',
      description: 'Short phrase shown next to the title (e.g. in the footer).',
    },
    pages: {
      type: 'array',
      title: 'Pages',
      // Render the pages array right under the site title, with each page
      // as a zoom-row in place. Without this, the user would have to
      // click into "Pages" to see them — a needless step on a top-level
      // collection that's the whole point of a site schema.
      'x-bliksys': { layout: 'inline' },
      items: { $ref: '#/$defs/Page' },
    },
    // Exercises object-hosted oneOf — same shape as the fstab schema that
    // surfaced the "Variant" label bug and the object-default bug. The
    // sub-schemas deliberately omit `type: 'object'` so defaultValueFor's
    // type inference from `properties` is covered by the demo path.
    navigation: {
      type: 'object',
      title: 'Navigation',
      oneOf: [{ $ref: '#/$defs/InlineNav' }, { $ref: '#/$defs/ExternalNav' }],
      discriminator: { propertyName: 'kind' },
    },
    defaultSEO: {
      title: 'Default SEO',
      description: 'Search-engine and social-card defaults for any page that does not set its own.',
      $ref: '#/$defs/SEO',
    },
    social: {
      title: 'Social profiles',
      type: 'object',
      properties: {
        twitter: { type: 'string', title: 'Twitter / X URL', format: 'uri' },
        linkedin: { type: 'string', title: 'LinkedIn URL', format: 'uri' },
        github: { type: 'string', title: 'GitHub URL', format: 'uri' },
      },
    },
  },
  $defs: {
    // ===================================================================
    // Reusable components — copy these into your own schema as needed.
    // ===================================================================

    /** A picture: src is mandatory; alt is mandatory for accessibility. */
    Image: {
      type: 'object',
      title: 'Image',
      'x-bliksys': { summaryField: 'alt' },
      required: ['src', 'alt'],
      properties: {
        src: {
          type: 'string',
          title: 'Source URL',
          format: 'uri',
          description: 'Direct URL to the image file.',
        },
        alt: {
          type: 'string',
          title: 'Alt text',
          description:
            'Describes the image for screen readers and when the image fails to load. ' +
            'Aim for a single descriptive sentence.',
        },
        caption: {
          type: 'string',
          title: 'Caption',
          description: 'Optional caption shown below the image.',
        },
        width: { type: 'integer', title: 'Width (px)', exclusiveMinimum: 0 },
        height: { type: 'integer', title: 'Height (px)', exclusiveMinimum: 0 },
      },
    },

    /** A link: text and href are mandatory. external defaults to false. */
    Link: {
      type: 'object',
      title: 'Link',
      'x-bliksys': { summaryField: 'text' },
      required: ['text', 'href'],
      properties: {
        text: { type: 'string', title: 'Text', description: 'The visible label.' },
        href: {
          type: 'string',
          title: 'Destination',
          description:
            'Either a path on this site (e.g. `/about`) or a full URL ' +
            '(e.g. `https://example.com`).',
        },
        external: {
          type: 'boolean',
          title: 'Opens in a new tab',
          description: 'When true, the link opens in a new browser tab.',
          default: false,
        },
      },
    },

    /** SEO and social-card metadata — referenced by Site's defaultSEO. */
    SEO: {
      type: 'object',
      title: 'SEO & social',
      description: 'Search-engine and social-card metadata.',
      'x-bliksys': {
        fieldGroups: [
          {
            id: 'search',
            title: 'Search engines',
            description:
              'Affects how the page appears in Google and other search results. ' +
              'Set `Block from search` to keep the page out of search indexes.',
            fields: ['description', 'canonical', 'noindex'],
          },
          {
            id: 'social',
            title: 'Social cards',
            description:
              'Affects how a URL preview looks when the page is shared on Twitter, ' +
              'LinkedIn, Slack, etc. Falls back to the page title and the first body ' +
              'image when blank.',
            fields: ['ogImage'],
          },
        ],
      },
      properties: {
        description: {
          type: 'string',
          title: 'Description',
          description: 'One- or two-sentence summary. Aim for under 160 characters.',
          maxLength: 200,
          'x-bliksys': { widget: 'textarea' },
        },
        canonical: {
          type: 'string',
          title: 'Canonical URL',
          description:
            'Override the canonical URL (used when this content is also published ' +
            'elsewhere). Leave blank for the default.',
          format: 'uri',
        },
        noindex: {
          type: 'boolean',
          title: 'Block from search',
          description: 'When true, search engines are asked not to index this page.',
          default: false,
        },
        ogImage: {
          title: 'Social card image',
          description: 'Recommended size: 1200×630.',
          $ref: '#/$defs/Image',
        },
      },
    },

    // ===================================================================
    // Navigation
    // ===================================================================
    InlineNav: {
      title: 'Inline navigation',
      'x-bliksys': { summaryField: 'kind' },
      required: ['kind', 'items'],
      properties: {
        kind: { const: 'inline' },
        items: {
          type: 'array',
          title: 'Menu items',
          description: 'Top-level navigation entries shown in the site header.',
          // Each nav item is a small object (label + pageId). The
          // collection is tiny and editing in context is the natural
          // flow; render inline rather than zoom-rows.
          'x-bliksys': { layout: 'inline' },
          items: {
            type: 'object',
            title: 'Menu item',
            'x-bliksys': { summaryField: 'label' },
            required: ['label', 'pageId'],
            properties: {
              label: { type: 'string', title: 'Label' },
              pageId: { type: 'string', title: 'Page ID' },
            },
          },
        },
      },
      additionalProperties: false,
    },
    ExternalNav: {
      title: 'External navigation',
      'x-bliksys': { summaryField: 'url' },
      required: ['kind', 'url'],
      properties: {
        kind: { const: 'external' },
        url: { type: 'string', format: 'uri', title: 'URL' },
      },
      additionalProperties: false,
    },

    // ===================================================================
    // Page — the canonical CMS document
    // ===================================================================
    Page: {
      type: 'object',
      title: 'Page',
      'x-bliksys': {
        summaryField: 'title',
        // Group the page's fields by author concern. Content is what
        // authors edit constantly and stays open; SEO & social and
        // Publishing are advanced and collapse but auto-expand once
        // they hold a value.
        fieldGroups: [
          {
            id: 'content',
            title: 'Content',
            description:
              'The body of the page. Title and slug identify it; blocks are the ' +
              'editable composition; keywords feed the site search.',
            fields: ['title', 'slug', 'keywords', 'blocks'],
          },
          {
            id: 'seo',
            title: 'SEO & social',
            description:
              'Page-specific SEO. Anything left blank falls back to the site-wide ' +
              'defaults set in Site → Defaults.',
            fields: ['description', 'ogImage', 'canonical', 'noindex'],
            collapsed: true,
          },
          {
            id: 'publishing',
            title: 'Publishing',
            description:
              'When the page goes live. Drafts are not built into the site; ' +
              'published pages are. A future "Published at" schedules the publish.',
            fields: ['status', 'publishedAt'],
            collapsed: true,
          },
        ],
      },
      required: ['title'],
      properties: {
        title: { type: 'string', title: 'Title' },
        slug: {
          type: 'string',
          title: 'Slug',
          description:
            'The URL fragment for this page. Lowercase letters, digits, and dashes; ' +
            'no spaces. Leave blank to derive from the title.',
          pattern: '^[a-z0-9-]*$',
        },
        keywords: {
          type: 'array',
          title: 'Keywords',
          items: { type: 'string' },
        },
        blocks: {
          type: 'array',
          title: 'Blocks',
          // Blocks are the structural payload of a page; show them
          // inline so the author sees the page as a stack of editable
          // blocks rather than as a flat collection that requires
          // zoom-in for each one.
          'x-bliksys': { layout: 'inline' },
          items: { $ref: '#/$defs/Block' },
        },
        description: {
          type: 'string',
          title: 'Meta description',
          description:
            'One- or two-sentence summary. Aim for under 160 characters. Falls back ' +
            'to the site-wide default when blank.',
          maxLength: 200,
          'x-bliksys': { widget: 'textarea' },
        },
        ogImage: {
          title: 'Social card image',
          description:
            'Image shown when the page URL is shared on Twitter, Facebook, etc. ' +
            'Recommended size: 1200×630. Falls back to the site-wide default.',
          $ref: '#/$defs/Image',
        },
        canonical: {
          type: 'string',
          title: 'Canonical URL',
          description:
            'Override the canonical URL (used when this content is also published ' +
            'elsewhere). Leave blank for the default.',
          format: 'uri',
        },
        noindex: {
          type: 'boolean',
          title: 'Block from search',
          description: 'When true, search engines are asked not to index this page.',
          default: false,
        },
        status: {
          type: 'string',
          title: 'Status',
          description: 'Drafts are excluded from the site; published pages appear at their slug.',
          enum: ['draft', 'published'],
          default: 'draft',
        },
        publishedAt: {
          type: 'string',
          title: 'Published at',
          description: 'When the page should go live. Future dates schedule the publish.',
          format: 'date-time',
        },
      },
    },

    // ===================================================================
    // Blocks — the page composition primitives
    //
    // Each variant uses summaryField so list-row previews and breadcrumbs
    // surface the meaningful content (heading text, paragraph text, alt
    // text, etc.) rather than "#1, #1, #1, #1, #1".
    // ===================================================================
    Block: {
      title: 'Block',
      description: 'A unit of page composition. Pick a type to start editing.',
      oneOf: [
        { $ref: '#/$defs/HeadingBlock' },
        { $ref: '#/$defs/ParagraphBlock' },
        { $ref: '#/$defs/ImageBlock' },
        { $ref: '#/$defs/QuoteBlock' },
        { $ref: '#/$defs/ButtonBlock' },
        { $ref: '#/$defs/DividerBlock' },
        { $ref: '#/$defs/ColumnsBlock' },
      ],
      discriminator: { propertyName: 'type' },
    },
    HeadingBlock: {
      type: 'object',
      title: 'Heading',
      'x-bliksys': { summaryField: 'content' },
      required: ['type', 'content'],
      properties: {
        type: { const: 'heading' },
        content: { type: 'string', title: 'Heading text' },
        level: {
          type: 'string',
          title: 'Heading level',
          description:
            'h1 is reserved for the page title — pick h2 for top-level sections, ' +
            'h3 for subsections.',
          enum: ['h2', 'h3', 'h4'],
          default: 'h2',
        },
      },
    },
    ParagraphBlock: {
      type: 'object',
      title: 'Paragraph',
      'x-bliksys': { summaryField: 'content' },
      required: ['type', 'content'],
      properties: {
        type: { const: 'paragraph' },
        content: {
          type: 'string',
          title: 'Paragraph text',
          'x-bliksys': { widget: 'textarea' },
        },
      },
    },
    ImageBlock: {
      type: 'object',
      title: 'Image',
      // The block surfaces the underlying Image component's alt text as
      // its summary — the breadcrumb shows the image's purpose, not "#3".
      'x-bliksys': { summaryField: 'alt' },
      required: ['type', 'src', 'alt'],
      properties: {
        type: { const: 'image' },
        src: {
          type: 'string',
          format: 'uri',
          title: 'Source URL',
          description: 'Direct URL to the image file.',
        },
        alt: {
          type: 'string',
          title: 'Alt text',
          description: 'Describes the image for screen readers and when it fails to load.',
        },
        caption: { type: 'string', title: 'Caption' },
        link: {
          title: 'Make it a link',
          description: 'When set, clicking the image navigates to this destination.',
          $ref: '#/$defs/Link',
        },
        alignment: {
          type: 'string',
          title: 'Alignment',
          enum: ['left', 'center', 'right'],
          default: 'center',
        },
      },
    },
    QuoteBlock: {
      type: 'object',
      title: 'Quote',
      'x-bliksys': { summaryField: 'content' },
      required: ['type', 'content'],
      properties: {
        type: { const: 'quote' },
        content: {
          type: 'string',
          title: 'Quote text',
          'x-bliksys': { widget: 'textarea' },
        },
        attribution: {
          type: 'string',
          title: 'Attribution',
          description: 'Who said it (optional).',
        },
      },
    },
    ButtonBlock: {
      type: 'object',
      title: 'Button',
      // No summaryField on the block itself — the link's `text` field is
      // the meaningful summary, surfaced via the Link def's summaryField
      // when the breadcrumb walks into the link.
      required: ['type', 'link'],
      properties: {
        type: { const: 'button' },
        link: {
          title: 'Link',
          $ref: '#/$defs/Link',
        },
        style: {
          type: 'string',
          title: 'Style',
          description: 'Visual prominence.',
          enum: ['primary', 'secondary', 'tertiary'],
          default: 'primary',
        },
      },
    },
    DividerBlock: {
      type: 'object',
      title: 'Divider',
      description: 'A horizontal rule that visually separates sections.',
      required: ['type'],
      properties: {
        type: { const: 'divider' },
      },
    },
    ColumnsBlock: {
      type: 'object',
      title: 'Columns',
      required: ['type', 'content'],
      properties: {
        type: { const: 'columns' },
        content: {
          type: 'array',
          title: 'Columns',
          // Columns appear side-by-side conceptually; render inline so
          // their content flows visibly under the parent.
          'x-bliksys': { layout: 'inline' },
          items: { $ref: '#/$defs/Column' },
        },
      },
    },
    Column: {
      type: 'object',
      title: 'Column',
      'x-bliksys': { summaryField: 'size' },
      required: ['type', 'size', 'content'],
      properties: {
        type: { const: 'column' },
        size: { type: 'string', title: 'Size', enum: ['auto', 'fit-content'] },
        content: {
          type: 'array',
          title: 'Content',
          // The leaf "content" array of a column is the actual editable
          // body — collapsing it behind a zoom-row would force a click
          // before the user could see anything.
          'x-bliksys': { layout: 'inline' },
          items: { $ref: '#/$defs/Block' },
        },
      },
    },
  },
};

export const demoData: JsonValue = {
  title: 'ACME Corp.',
  tagline: 'Solutions that work — even at the bottom of a canyon.',
  navigation: {
    kind: 'inline',
    items: [
      { label: 'About', pageId: 'about' },
      { label: 'Products', pageId: 'products' },
      { label: 'Contact', pageId: 'contact' },
    ],
  },
  defaultSEO: {
    description: 'ACME Corp. — innovative solutions for modern challenges, since 1949.',
    ogImage: {
      src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200',
      alt: 'ACME Corp. headquarters at sunset',
    },
  },
  social: {
    twitter: 'https://twitter.com/acme',
    linkedin: 'https://linkedin.com/company/acme',
  },
  pages: [
    {
      title: 'About us',
      slug: 'about',
      keywords: ['acme corp', 'about', 'history'],
      // Page-level SEO override + a published date — both collapsed
      // sections auto-expand because they hold values, demonstrating
      // the auto-expand UX.
      description:
        'Learn about ACME Corp.: our mission, history, and the team building solutions ' +
        'that occasionally explode.',
      ogImage: {
        src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200',
        alt: 'ACME Corp. office building',
      },
      status: 'published',
      publishedAt: '2024-03-15T09:00:00Z',
      blocks: [
        {
          type: 'heading',
          level: 'h2',
          content: 'About us',
        },
        {
          type: 'paragraph',
          content:
            'ACME Corp. is a leader in delivering Wile E. Coyote solutions — the kind ' +
            'that work right up until the moment they spectacularly do not.',
        },
        {
          type: 'columns',
          content: [
            {
              type: 'column',
              size: 'auto',
              content: [
                { type: 'heading', level: 'h3', content: 'Our mission' },
                {
                  type: 'paragraph',
                  content:
                    'Build tools that ALMOST catch the roadrunner. The chase is the ' + 'product.',
                },
                {
                  type: 'quote',
                  content: 'Beep beep.',
                  attribution: 'A satisfied non-customer',
                },
              ],
            },
            {
              type: 'column',
              size: 'fit-content',
              content: [
                {
                  type: 'image',
                  src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400',
                  alt: 'office-building',
                  alignment: 'center',
                },
              ],
            },
          ],
        },
        { type: 'divider' },
        {
          type: 'button',
          link: {
            text: 'See our products',
            href: '/products',
          },
          style: 'primary',
        },
      ],
    },
    {
      title: 'Products',
      slug: 'products',
      keywords: ['products', 'catalog'],
      // No SEO / publishing data on this page — sections stay collapsed,
      // demonstrating the contrast with the "About us" page above.
      status: 'draft',
      blocks: [
        { type: 'heading', level: 'h2', content: 'Our catalog' },
        {
          type: 'paragraph',
          content: 'Coming soon. (Subject to gravity.)',
        },
      ],
    },
    {
      title: 'Contact',
      slug: 'contact',
      status: 'draft',
      blocks: [
        { type: 'heading', level: 'h2', content: 'Get in touch' },
        {
          type: 'paragraph',
          content: 'Reach us by mail at ACME Corp., Box 1, Desert Plateau, USA.',
        },
      ],
    },
  ],
};
